import { useEffect, useState } from 'react'
import ComplianceExport from '../components/settings/ComplianceExport'
import { Link, useLocation } from 'react-router-dom'
import toast from 'react-hot-toast'
import {
  ShieldCheckIcon,
  ArrowDownTrayIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  IdentificationIcon,
  PaintBrushIcon,
} from '@heroicons/react/24/outline'
import { complianceAPI, creatorProfileAPI } from '../services/api'
import type {
  ConsentType,
  ConsentStatus,
  DisclaimerResponse,
  CreatorVettingResponse,
  CreatorProfile,
  DomainVerificationToken,
} from '../types'
import LoadingScreen from '../components/LoadingScreen'
import VettingApplicationForm from '../components/monetization/VettingApplicationForm'
import { useAuthStore } from '../store/authStore'
import ConfirmDialog from '../components/ConfirmDialog'
import Tooltip from '../components/Tooltip'
import useKrakenAccess from '../hooks/useKrakenAccess'
import ThemeToggle from '../components/ThemeToggle'
import SubscriptionEmailsSetting from '../components/SubscriptionEmailsSetting'
import DeveloperSettings from '../components/DeveloperSettings'
import { useThemePreference } from '../theme/themePreference'
import { SETTINGS_GLOSSARY } from '../config/glossary'
import { mediaUrl } from '../utils/mediaUrl'

const VETTING_STATUS_LABEL: Record<CreatorVettingResponse['status'], string> = {
  not_submitted: 'Not submitted',
  pending: 'Under review',
  approved: 'Approved',
  rejected: 'Rejected',
}

const VETTING_STATUS_CLASSES: Record<CreatorVettingResponse['status'], string> = {
  not_submitted: 'bg-gray-100 text-gray-700',
  pending: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
}

const TOGGLEABLE_CONSENTS: { type: ConsentType; label: string; description: string; hint?: string }[] = [
  {
    type: 'marketing_emails',
    label: 'Marketing emails',
    description: 'Receive product updates, tips, and promotional emails.',
  },
  {
    type: 'data_processing',
    label: 'Data processing for personalization',
    description: 'Allow us to use your activity to personalize recommendations.',
    hint: SETTINGS_GLOSSARY.personalization,
  },
  {
    type: 'cookies_analytics',
    label: 'Analytics cookies',
    description: 'Help us improve FinCreator by sharing anonymized usage data.',
    hint: SETTINGS_GLOSSARY.analyticsCookies,
  },
]

const READONLY_CONSENTS: { type: ConsentType; label: string; href: string }[] = [
  { type: 'terms_of_service', label: 'Terms of Service', href: '/terms' },
  { type: 'privacy_policy', label: 'Privacy Policy', href: '/privacy' },
]

const Settings = () => {
  // The Engine page sits behind the same live-trading gate as its actions.
  const canTrade = useKrakenAccess()
  const { canChoose: canChooseTheme } = useThemePreference()
  // Scheduled drafts for FinCreator's house accounts: admin only, as on Content.
  const isAdmin = useAuthStore((state) => !!state.user?.is_superuser)
  const location = useLocation()
  const [loading, setLoading] = useState(true)
  const [consents, setConsents] = useState<Record<string, ConsentStatus>>({})
  const [savingConsent, setSavingConsent] = useState<ConsentType | null>(null)
  const [exporting, setExporting] = useState(false)
  const [disclaimer, setDisclaimer] = useState<DisclaimerResponse | null>(null)
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [vetting, setVetting] = useState<CreatorVettingResponse | null>(null)
  const [showVettingForm, setShowVettingForm] = useState(false)
  const [myProfile, setMyProfile] = useState<CreatorProfile | null>(null)
  const [savingBranding, setSavingBranding] = useState(false)
  const [brandingForm, setBrandingForm] = useState({
    logo_url: '',
    brand_color_primary: '',
    brand_color_secondary: '',
    custom_domain: '',
  })
  const [verificationToken, setVerificationToken] = useState<DomainVerificationToken | null>(null)
  const [requestingToken, setRequestingToken] = useState(false)
  const [verifyingDomain, setVerifyingDomain] = useState(false)

  useEffect(() => {
    loadSettings()
  }, [])

  const loadSettings = async () => {
    try {
      setLoading(true)
      const [consentStatus, disclaimerText, vettingStatus, profile] = await Promise.all([
        complianceAPI.getConsentStatus(),
        complianceAPI.getDisclaimer('general_content'),
        complianceAPI.getMyVetting(),
        creatorProfileAPI.getMyProfile(),
      ])
      setConsents(consentStatus)
      setDisclaimer(disclaimerText)
      setVetting(vettingStatus)
      setMyProfile(profile)
      setBrandingForm({
        logo_url: profile.logo_url ?? '',
        brand_color_primary: profile.brand_color_primary ?? '',
        brand_color_secondary: profile.brand_color_secondary ?? '',
        custom_domain: profile.custom_domain ?? '',
      })
    } catch (error) {
      console.error('Error loading settings:', error)
      toast.error('Failed to load settings')
    } finally {
      setLoading(false)
    }
  }

  const handleSaveBranding = async () => {
    const hexPattern = /^#[0-9A-Fa-f]{6}$/
    if (brandingForm.brand_color_primary && !hexPattern.test(brandingForm.brand_color_primary)) {
      toast.error('Primary color must be a hex value like #0EA5E9')
      return
    }
    if (brandingForm.brand_color_secondary && !hexPattern.test(brandingForm.brand_color_secondary)) {
      toast.error('Secondary color must be a hex value like #0EA5E9')
      return
    }

    try {
      setSavingBranding(true)
      const domainChanged = brandingForm.custom_domain.trim() !== (myProfile?.custom_domain ?? '')
      const updated = await creatorProfileAPI.updateMyProfile({
        logo_url: brandingForm.logo_url.trim() || undefined,
        brand_color_primary: brandingForm.brand_color_primary.trim() || undefined,
        brand_color_secondary: brandingForm.brand_color_secondary.trim() || undefined,
        custom_domain: brandingForm.custom_domain.trim() || undefined,
      })
      setMyProfile(updated)
      if (domainChanged) {
        setVerificationToken(null)
      }
      toast.success('Branding saved')
    } catch (error) {
      console.error('Error saving branding:', error)
      toast.error('Failed to save branding')
    } finally {
      setSavingBranding(false)
    }
  }

  const handleRequestVerificationToken = async () => {
    try {
      setRequestingToken(true)
      const token = await creatorProfileAPI.requestDomainVerificationToken()
      setVerificationToken(token)
    } catch (error) {
      console.error('Error requesting verification token:', error)
      toast.error('Failed to generate a verification token. Save a custom domain first.')
    } finally {
      setRequestingToken(false)
    }
  }

  const handleVerifyDomain = async () => {
    try {
      setVerifyingDomain(true)
      const result = await creatorProfileAPI.verifyDomain()
      if (result.verified) {
        setMyProfile((prev) => (prev ? { ...prev, custom_domain_verified: true } : prev))
        toast.success('Domain verified')
      } else {
        toast.error('Verification record not found yet. DNS changes can take a few minutes to propagate.')
      }
    } catch (error) {
      console.error('Error verifying domain:', error)
      toast.error('Failed to verify domain')
    } finally {
      setVerifyingDomain(false)
    }
  }

  const handleToggleConsent = async (consentType: ConsentType, granted: boolean) => {
    try {
      setSavingConsent(consentType)
      const updated = await complianceAPI.updateConsent(consentType, granted)
      setConsents((prev) => ({
        ...prev,
        [consentType]: {
          granted: updated.granted,
          version: updated.version,
          updated_at: updated.created_at,
        },
      }))
      toast.success('Preference saved')
    } catch (error) {
      console.error('Error updating consent:', error)
      toast.error('Failed to save preference')
    } finally {
      setSavingConsent(null)
    }
  }

  const handleExportData = async () => {
    try {
      setExporting(true)
      const data = await complianceAPI.exportMyData()
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `fincreator-data-export-${new Date().toISOString().slice(0, 10)}.json`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
      toast.success('Your data export has started downloading')
    } catch (error) {
      console.error('Error exporting data:', error)
      toast.error('Failed to export data')
    } finally {
      setExporting(false)
    }
  }

  const handleDeleteAccount = async () => {
    try {
      setDeleting(true)
      const request = await complianceAPI.requestAccountDeletion()
      toast.success(`Deletion request submitted (status: ${request.status})`)
      setShowDeleteConfirm(false)
    } catch (error) {
      console.error('Error requesting account deletion:', error)
      toast.error('Failed to request account deletion')
    } finally {
      setDeleting(false)
    }
  }

  // Old links to /settings#content-automation land on the moved-card; scroll there once rendered.
  useEffect(() => {
    if (loading || !location.hash) return
    document.getElementById(location.hash.slice(1))?.scrollIntoView?.({ block: 'start' })
  }, [loading, location.hash])

  if (loading) return <LoadingScreen />

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="text-sm text-gray-500 mt-1">
          Manage your privacy preferences and account data.
        </p>
      </div>

      {/* Moved here from the sidebar footer, where it pushed the menu's last
          items off a phone screen. Hidden with the dark theme built out. */}
      {canChooseTheme && (
        <section className="bg-white rounded-lg shadow p-6" aria-labelledby="appearance-heading">
          <h2 id="appearance-heading" className="text-lg font-semibold text-gray-900">
            Appearance
          </h2>
          <div className="mt-3 max-w-sm">
            <ThemeToggle />
          </div>
        </section>
      )}

      {canTrade && (
        <Link
          to="/settings/engine"
          className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition hover:border-accent"
          data-testid="engine-settings-card"
        >
          <span>
            <span className="block font-semibold text-gray-900">Engine</span>
            <span className="block text-sm text-gray-500">Scanner status and manual actions (admin).</span>
          </span>
          <span className="text-accent" aria-hidden="true">→</span>
        </Link>
      )}

      {/* The recap auto-draft schedule moved to Content → Automation: admin only */}
      {isAdmin && (
        <Link
          to="/admin/vetting"
          className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition hover:border-accent"
          data-testid="admin-vetting-link"
        >
          <span className="text-sm text-gray-900">Admin: review creator applications to monetize</span>
          <span className="text-accent" aria-hidden="true">→</span>
        </Link>
      )}

      {isAdmin && (
        <Link
          id="content-automation"
          to="/content/automation"
          className="flex items-center justify-between rounded-lg border border-gray-200 bg-white p-4 shadow-sm transition hover:border-accent"
          data-testid="content-automation-moved-card"
        >
          <span className="text-sm text-gray-900">Content automation moved to Content → Automation</span>
          <span className="text-accent" aria-hidden="true">→</span>
        </Link>
      )}

      <section className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <ShieldCheckIcon className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Privacy &amp; Consent</h2>
        </div>

        <div className="space-y-4">
          {TOGGLEABLE_CONSENTS.map((item) => {
            const status = consents[item.type]
            const granted = status?.granted ?? false
            return (
              <div key={item.type} className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-gray-900">
                    {item.hint ? <Tooltip content={item.hint}>{item.label}</Tooltip> : item.label}
                  </p>
                  <p className="text-xs text-gray-500">{item.description}</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={granted}
                  aria-label={item.label}
                  disabled={savingConsent === item.type}
                  onClick={() => handleToggleConsent(item.type, !granted)}
                  className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
                    granted ? 'bg-primary-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      granted ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
            )
          })}

          <div className="pt-2 border-t">
            {READONLY_CONSENTS.map((item) => {
              const status = consents[item.type]
              return (
                <div key={item.type} className="flex items-center justify-between py-2 text-sm">
                  <Link to={item.href} className="text-primary-600 hover:underline">
                    {item.label}
                  </Link>
                  <span className="text-gray-500">
                    {status?.granted ? 'Accepted' : 'Not yet accepted'}
                  </span>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      <SubscriptionEmailsSetting />

      <section className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <IdentificationIcon className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              <Tooltip content={SETTINGS_GLOSSARY.creatorVerification}>Creator Verification</Tooltip>
            </h2>
          </div>
          {vetting && (
            <span
              className={`text-xs font-medium px-2 py-1 rounded-full ${VETTING_STATUS_CLASSES[vetting.status]}`}
            >
              {VETTING_STATUS_LABEL[vetting.status]}
            </span>
          )}
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Monetization features (subscription tiers, marketplace products, and signal
          packages) require an approved creator-vetting application. This is a
          self-attestation reviewed by our team -- it does not block posting free
          content or signals. Currently available for creators based in the United
          States.
        </p>

        {vetting?.status === 'rejected' && (
          <div className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
            Your application was not approved.
            {vetting.review_notes ? ` ${vetting.review_notes}` : ''}
          </div>
        )}

        {vetting?.status === 'pending' && (
          <p className="text-sm text-gray-600">
            Your application is under review. We&apos;ll notify you once it&apos;s
            decided.
          </p>
        )}

        {vetting?.status === 'approved' && (
          <p className="text-sm text-green-700">
            You&apos;re approved to enable monetization features.
          </p>
        )}

        {(vetting?.status === 'not_submitted' || vetting?.status === 'rejected') &&
          !showVettingForm && (
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setShowVettingForm(true)}
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium"
              >
                {vetting?.status === 'rejected' ? 'Resubmit application' : 'Apply to monetize'}
              </button>
              <Tooltip content={SETTINGS_GLOSSARY.applyToMonetize} label="Apply to monetize" />
            </div>
          )}

        {showVettingForm && (
          <div className="mt-2">
            <VettingApplicationForm
              resubmit={vetting?.status === 'rejected'}
              onSubmitted={(result) => {
                setVetting(result)
                setShowVettingForm(false)
              }}
              onCancel={() => setShowVettingForm(false)}
            />
          </div>
        )}
      </section>

      <section className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <PaintBrushIcon className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              <Tooltip content={SETTINGS_GLOSSARY.whiteLabel}>White-Label Branding</Tooltip>
            </h2>
          </div>
          {myProfile?.custom_domain && (
            <span
              className={`text-xs font-medium px-2 py-1 rounded-full ${
                myProfile.custom_domain_verified
                  ? 'bg-green-100 text-green-800'
                  : 'bg-yellow-100 text-yellow-800'
              }`}
            >
              {myProfile.custom_domain_verified ? 'Domain verified' : 'Domain not verified'}
            </span>
          )}
        </div>

        <p className="text-sm text-gray-600 mb-4">
          Customize your logo, brand colors, and connect a custom domain so subscribers see your
          own storefront instead of the FinCreator platform shell.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Logo URL</label>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={brandingForm.logo_url}
                onChange={(e) =>
                  setBrandingForm((prev) => ({ ...prev, logo_url: e.target.value }))
                }
                placeholder="https://cdn.example.com/logo.png"
                className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              {brandingForm.logo_url && (
                <img
                  src={mediaUrl(brandingForm.logo_url)}
                  alt="Logo preview"
                  className="h-10 w-10 rounded object-contain border border-gray-200"
                  onError={(e) => {
                    e.currentTarget.style.visibility = 'hidden'
                  }}
                />
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Primary brand color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={
                    /^#[0-9A-Fa-f]{6}$/.test(brandingForm.brand_color_primary)
                      ? brandingForm.brand_color_primary
                      : '#0ea5e9'
                  }
                  onChange={(e) =>
                    setBrandingForm((prev) => ({ ...prev, brand_color_primary: e.target.value }))
                  }
                  className="h-9 w-9 rounded border border-gray-300"
                />
                <input
                  type="text"
                  value={brandingForm.brand_color_primary}
                  onChange={(e) =>
                    setBrandingForm((prev) => ({ ...prev, brand_color_primary: e.target.value }))
                  }
                  placeholder="#0EA5E9"
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Secondary brand color
              </label>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={
                    /^#[0-9A-Fa-f]{6}$/.test(brandingForm.brand_color_secondary)
                      ? brandingForm.brand_color_secondary
                      : '#0c4a6e'
                  }
                  onChange={(e) =>
                    setBrandingForm((prev) => ({
                      ...prev,
                      brand_color_secondary: e.target.value,
                    }))
                  }
                  className="h-9 w-9 rounded border border-gray-300"
                />
                <input
                  type="text"
                  value={brandingForm.brand_color_secondary}
                  onChange={(e) =>
                    setBrandingForm((prev) => ({
                      ...prev,
                      brand_color_secondary: e.target.value,
                    }))
                  }
                  placeholder="#0C4A6E"
                  className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-1 mb-1">
              <label className="text-sm font-medium text-gray-700">Custom domain</label>
              <Tooltip content={SETTINGS_GLOSSARY.customDomain} label="a custom domain" />
            </div>
            <input
              type="text"
              value={brandingForm.custom_domain}
              onChange={(e) =>
                setBrandingForm((prev) => ({ ...prev, custom_domain: e.target.value }))
              }
              placeholder="creator.yourdomain.com"
              className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          <button
            type="button"
            onClick={handleSaveBranding}
            disabled={savingBranding}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
          >
            {savingBranding ? 'Saving...' : 'Save branding'}
          </button>

          {myProfile?.custom_domain && !myProfile.custom_domain_verified && (
            <div className="pt-4 border-t space-y-3">
              <p className="text-sm text-gray-600">
                Prove you control <span className="font-medium">{myProfile.custom_domain}</span>{' '}
                before it can serve your storefront.
              </p>

              {!verificationToken ? (
                <button
                  type="button"
                  onClick={handleRequestVerificationToken}
                  disabled={requestingToken}
                  className="px-4 py-2 border border-primary-600 text-primary-600 rounded-md hover:bg-primary-50 transition-colors font-medium disabled:opacity-50"
                >
                  {requestingToken ? 'Generating...' : 'Get verification instructions'}
                </button>
              ) : (
                <div className="text-sm text-gray-700 bg-gray-50 border border-gray-200 rounded-md p-3 space-y-2">
                  <p>Add either of the following, then click verify:</p>
                  <div>
                    <p className="text-xs font-medium text-gray-500">DNS TXT record</p>
                    <p className="font-mono text-xs break-all">
                      {verificationToken.dns_record_name} = {verificationToken.dns_record_value}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-medium text-gray-500">Or a well-known file</p>
                    <p className="font-mono text-xs break-all">
                      {verificationToken.well_known_path} containing{' '}
                      {verificationToken.well_known_value}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={handleVerifyDomain}
                    disabled={verifyingDomain}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
                  >
                    {verifyingDomain ? 'Verifying...' : "I've added this - Verify"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      <DeveloperSettings />

      <section className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <ArrowDownTrayIcon className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-gray-900">Your Data</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Download a copy of all the data associated with your account, including your profile,
          portfolios, subscriptions, payments, and signals.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleExportData}
            disabled={exporting}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors font-medium disabled:opacity-50"
          >
            {exporting ? 'Preparing export...' : 'Export my data'}
          </button>
          <Tooltip content={SETTINGS_GLOSSARY.exportData} label="Export my data" />
        </div>
      </section>

      <ComplianceExport />

      <section className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <ExclamationTriangleIcon className="h-5 w-5 text-danger-500" />
          <h2 className="text-lg font-semibold text-gray-900">Delete Account</h2>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Request permanent deletion of your account. This submits a deletion request for review;
          it does not happen instantly.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowDeleteConfirm(true)}
            className="px-4 py-2 border border-danger-500 text-danger-500 rounded-md hover:bg-danger-50 transition-colors font-medium"
          >
            Delete my account
          </button>
          <Tooltip content={SETTINGS_GLOSSARY.deleteAccount} label="Delete my account" />
        </div>
      </section>

      {disclaimer && (
        <section className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center gap-2 mb-4">
            <DocumentTextIcon className="h-5 w-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">Disclaimer</h2>
          </div>
          <p className="text-xs text-gray-500 whitespace-pre-wrap">{disclaimer.text}</p>
        </section>
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete your account"
        message="This will submit a request to permanently delete your account and associated data. This action cannot be undone once processed. Continue?"
        confirmLabel="Delete account"
        confirming={deleting}
        danger
        onConfirm={handleDeleteAccount}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  )
}

export default Settings
